import { Flashbar, FlashbarProps } from "@amzn/awsui-components-react";
import { v4 as uuidv4 } from "uuid";
import { useState } from "react";
import { ErrorProps } from "interfaces";
import { FLASH_BAR_TEST_ID, getTestId } from "helpers";

const SCROLL_TIMEOUT = 100;

/**
 * Custom hook for managing flashbar messages with optional persistence and stacking
 *
 * @param {boolean} stackItems - Whether to stack multiple flashbar items vertically
 * @param {boolean} autoScroll - Whether to automatically scroll to flashbar when new messages are added
 * @return {object} - Object containing Flashbar component, message management functions, and utilities
 */
const useFlashbar = (stackItems: boolean) => {
    const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

    /**
     * Scrolls to the flashbar element
     */
    const scrollToFlashbar = () => {
        requestAnimationFrame(() => {
            const flashbar = document.querySelector(getTestId(FLASH_BAR_TEST_ID));
            flashbar?.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            });
        });
    };

    /**
     * Generates the Flashbar component with current items
     *
     * @return {JSX.Element} - Flashbar component configured with current items and stacking option
     */
    const generateFlashbar = () => (
        <Flashbar data-testid={FLASH_BAR_TEST_ID} items={items} stackItems={stackItems}></Flashbar>
    );

    /**
     * Removes a specific flashbar item by its ID
     *
     * @param {string} id - The unique identifier of the flashbar item to remove
     * @return {void}
     */
    const removeItem = (id: string) => {
        setItems((items) => items.filter((item) => item.id !== id));
    };

    /**
     * Creates and adds a new flashbar message to the items list
     *
     * @param {FlashbarProps.MessageDefinition} message - The message definition object containing type, content, and other properties
     * @return {string} - The unique ID assigned to the created message
     */
    const generateMessage = (message: FlashbarProps.MessageDefinition, autoScroll: boolean = false): string => {
        if (!message.id) {
            message.id = uuidv4();
        }

        if (message.dismissible === undefined || message.dismissible) {
            message.onDismiss = () => removeItem(message.id!);
        }

        setItems((items) => [...items, message]);

        if (autoScroll) {
            setTimeout(scrollToFlashbar, SCROLL_TIMEOUT);
        }

        return message.id;
    };

    const generateError = (error?: ErrorProps, autoScroll: boolean = false): string | undefined => {
        if (!error) return undefined;

        const message: FlashbarProps.MessageDefinition = {
            header: error.name,
            type: "error",
            content: error.content,
            dismissible: true,
            id: uuidv4(),
            onDismiss: () => removeItem(message.id!),
        };
        message.id = uuidv4();

        setItems((items) => [...items, message]);

        if (autoScroll) {
            setTimeout(scrollToFlashbar, SCROLL_TIMEOUT);
        }

        return message.id;
    };

    /**
     * Clears all flashbar messages and removes them from session storage if persistence is enabled
     *
     * @return {void}
     */
    const clearMessages = () => {
        setItems([]);
    };

    return {
        Flashbar: generateFlashbar(),
        generateMessage,
        generateError,
        clearMessages,
        removeItem,
    };
};

export default useFlashbar;
